/**
 * Transactions (§13.3) — "the whole history in one fast table ... virtualized;
 * must stay instant at tens of thousands of rows."
 *
 * How that requirement is met, since it drives every decision here:
 *
 *   * **The server pages.** Filters, sorting and counts happen in SQL; the
 *     browser never receives the full history. The footer totals come from the
 *     server too, so they describe the whole filtered set rather than the
 *     visible slice — a footer that only sums what you can see is worse than no
 *     footer.
 *   * **The rows virtualize.** Only the visible window is in the DOM, so
 *     scrolling stays smooth regardless of page size.
 *   * **Filters are debounced and reset the offset**, because changing a filter
 *     while sitting on page 40 of the old result is meaningless.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Clock, Download, Filter, Loader2, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Badge,
  Button,
  CardSwatch,
  EmptyState,
  Input,
  PageHeader,
  Panel,
  Segmented,
} from "@/components/ui/primitives";
import {
  cardsApi,
  transactionsApi,
  type Card,
  type CardTheme,
  type Transaction,
  type TransactionFilters,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";

const PAGE_SIZE = 200;
const ROW_HEIGHT = 52;

export function TransactionsPage() {
  // `?q=` pre-fills the search — the ⌘K palette's "search transactions for …"
  // lands here. Read on every change so a second palette search while already
  // on this page still applies.
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const [search, setSearch] = useState(q);
  const [debounced, setDebounced] = useState(q);
  useEffect(() => {
    setSearch(q);
    setDebounced(q);
  }, [q]);
  const [cardIds, setCardIds] = useState<string[]>([]);
  const [direction, setDirection] = useState<"debit" | "credit" | undefined>();
  const [offset, setOffset] = useState(0);

  // Debounce the search so a fast typist doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change invalidates the current position in the result set.
  useEffect(() => setOffset(0), [debounced, cardIds, direction]);

  const filters: TransactionFilters = useMemo(
    () => ({
      search: debounced || undefined,
      card_id: cardIds.length ? cardIds : undefined,
      direction,
      offset,
      limit: PAGE_SIZE,
    }),
    [debounced, cardIds, direction, offset],
  );

  const { data: cards } = useQuery({
    queryKey: ["cards", true],
    queryFn: () => cardsApi.list(true),
  });

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["transactions", filters],
    queryFn: () => transactionsApi.list(filters),
    // Keep the old rows on screen while the next page loads, so the table
    // doesn't blink to empty on every filter change.
    placeholderData: keepPreviousData,
  });

  const hasFilters = Boolean(debounced || cardIds.length || direction);
  const clearAll = () => {
    setSearch("");
    setCardIds([]);
    setDirection(undefined);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          data
            ? `${data.total.toLocaleString("en-IN")} row${data.total === 1 ? "" : "s"}${
                hasFilters ? " matching" : ""
              }`
            : "Every statement and alert"
        }
        title="Transactions"
        description="The whole history in one place. Alert-derived rows are marked provisional until their statement lands."
        actions={
          <Button
            asChild
            variant="default"
            title="Exports everything matching the current filters, not just this page."
          >
            <a href={transactionsApi.exportUrl(filters)} download>
              <Download className="h-4 w-4" aria-hidden />
              Export CSV
            </a>
          </Button>
        }
      />

      {/* --- Filters ------------------------------------------------------ */}
      <Panel className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[14rem] flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search description or merchant"
              className="pl-9"
              aria-label="Search transactions"
            />
          </div>

          <Segmented
            label="Direction"
            value={direction ?? "all"}
            onChange={(v) => setDirection(v === "all" ? undefined : (v as "debit" | "credit"))}
            options={[
              { value: "all", label: "All" },
              { value: "debit", label: "Spend" },
              { value: "credit", label: "Credits" },
            ]}
          />

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              <X className="h-4 w-4" aria-hidden />
              Clear
            </Button>
          )}
        </div>

        {(cards?.length ?? 0) > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
            {cards!.map((c) => {
              const on = cardIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setCardIds((prev) =>
                      on ? prev.filter((x) => x !== c.id) : [...prev, c.id],
                    )
                  }
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border py-1 pl-1.5 pr-2.5 text-xs transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
                    on
                      ? "border-brass/50 bg-brass/10 text-ink"
                      : "border-hairline text-muted hover:border-hairline-strong hover:text-ink",
                  )}
                >
                  <CardSwatch theme={c.theme} />
                  {c.nickname || c.product_name || c.issuer}
                </button>
              );
            })}
          </div>
        )}
      </Panel>

      {/* --- Table -------------------------------------------------------- */}
      {isError ? (
        <Panel className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm text-ink">Couldn't load transactions.</p>
              <p className="mt-0.5 text-xs text-muted">{(error as Error).message}</p>
            </div>
            <Button variant="default" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Panel>
      ) : isLoading ? (
        <Panel className="space-y-2 p-4">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="skeleton h-9 w-full" />
          ))}
        </Panel>
      ) : !data || data.items.length === 0 ? (
        <Panel>
          <EmptyState
            icon={Search}
            title={hasFilters ? "Nothing matches" : "No transactions yet"}
            body={
              hasFilters
                ? "No transactions match these filters."
                : "Transactions appear as your statements are parsed."
            }
            action={
              hasFilters ? (
                <Button variant="default" size="sm" onClick={clearAll}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        </Panel>
      ) : (
        <Panel className="overflow-hidden">
          <VirtualTable rows={data.items} cards={cards ?? []} busy={isFetching} />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline bg-base/40 px-4 py-3 text-xs">
            <span className="text-faint">
              Showing {offset + 1}–{offset + data.items.length} of{" "}
              {data.total.toLocaleString("en-IN")}
            </span>

            <span className="flex items-center gap-4 tabular-nums">
              <span className="text-muted">
                Spend <span className="font-medium text-ink">{formatMoney(data.total_debits)}</span>
              </span>
              <span className="text-muted">
                Credits <span className="font-medium text-paid">{formatMoney(data.total_credits)}</span>
              </span>
            </span>

            <span className="flex gap-1">
              <Button
                variant="default"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                variant="default"
                size="sm"
                disabled={offset + PAGE_SIZE >= data.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </span>
          </div>
        </Panel>
      )}
    </div>
  );
}

function VirtualTable({
  rows,
  cards,
  busy,
}: {
  rows: Transaction[];
  cards: Card[];
  busy: boolean;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const cardName = useMemo(() => {
    const map = new Map<string, { name: string; theme: CardTheme | null }>();
    for (const c of cards)
      map.set(c.id, { name: c.nickname || c.product_name || c.issuer, theme: c.theme });
    return map;
  }, [cards]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  return (
    <div className="relative">
      {busy && (
        <div className="absolute right-4 top-3 z-10">
          <Loader2 className="h-4 w-4 animate-spin text-faint" aria-hidden />
        </div>
      )}

      {/* Header sits outside the scroll container so it stays put. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-hairline px-4 py-2.5 sm:grid-cols-[7rem_minmax(0,1fr)_10rem_7rem]">
        <span className="label-micro hidden sm:block">Date</span>
        <span className="label-micro">Description</span>
        <span className="label-micro hidden sm:block">Card</span>
        <span className="label-micro text-right">Amount</span>
      </div>

      <div ref={parentRef} className="max-h-[60vh] overflow-auto">
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((item) => {
            const t = rows[item.index]!;
            const isCredit = t.direction === "credit";
            return (
              <div
                key={t.id}
                className="absolute inset-x-0 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-hairline px-4 text-sm transition-colors hover:bg-raised/60 sm:grid-cols-[7rem_minmax(0,1fr)_10rem_7rem]"
                style={{ height: item.size, transform: `translateY(${item.start}px)` }}
              >
                <span
                  className={cn(
                    "hidden tabular-nums sm:block",
                    // §2.7 — an alert-derived row is a guess at a transaction,
                    // not a posted one, and must never look like statement
                    // truth. It stays here until the statement supersedes it.
                    t.is_provisional ? "text-faint" : "text-muted",
                  )}
                >
                  {formatDate(t.txn_date)}
                </span>
                <span className="flex min-w-0 flex-col justify-center sm:flex-row sm:items-center sm:justify-start sm:gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn("truncate", t.is_provisional ? "text-muted" : "text-ink")}
                    title={t.description_raw}
                  >
                    {t.merchant || t.description_raw}
                  </span>
                  {t.is_provisional && (
                    <Badge tone="neutral" title="Seen in a transaction alert. It will be replaced by the statement row when the bill arrives.">
                      <Clock className="h-2.5 w-2.5" aria-hidden />
                      provisional
                    </Badge>
                  )}
                  {t.flags &&
                    Object.keys(t.flags).map((flag) => (
                      <Badge
                        key={flag}
                        tone={flag === "cashback" ? "brass" : "neutral"}
                        className="hidden shrink-0 whitespace-nowrap sm:inline-flex"
                      >
                        {flag.replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </span>
                  {/* Phone: date and card ride under the description. */}
                  <span className="truncate text-[11.5px] text-faint sm:hidden">
                    {formatDate(t.txn_date)} · {cardName.get(t.card_id)?.name ?? "—"}
                  </span>
                </span>
                <span className="hidden min-w-0 items-center gap-2 text-xs text-faint sm:flex">
                  <CardSwatch theme={cardName.get(t.card_id)?.theme ?? null} />
                  <span className="truncate">{cardName.get(t.card_id)?.name ?? "—"}</span>
                </span>
                <span
                  className={cn(
                    "text-right font-medium tabular-nums",
                    isCredit ? "text-paid" : "text-ink",
                    t.is_provisional && "opacity-70",
                  )}
                >
                  {isCredit ? "+" : ""}
                  {formatMoney(t.amount)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
